import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@/contexts/PageContext';
import { usePartners, useProducts, usePartnershipsAccess, canEditSection } from '@/hooks/usePartnerships';
import { ProductCard } from '@/components/partnerships/ProductCard';
import { ProductFormDialog } from '@/components/partnerships/ProductFormDialog';
import { PARTNER_CONFIG } from '@/components/partnerships/PartnerTheme';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { PartnerSlug, ProductCategory, Product } from '../../../../shared/types/partnerships';

const CATEGORIES: ProductCategory[] = ['Motors', 'Experience', 'Amplification', 'Enhancers', 'Phygital'];

export function PartnerProductsPage() {
  const { t } = useTranslation();
  const { partner: slug } = useParams<{ partner: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ProductCategory | 'all'>('all');
  const [editProduct, setEditProduct] = useState<Product | undefined>();
  const [createProduct, setCreateProduct] = useState(false);

  const { data: access } = usePartnershipsAccess();
  const { data: partners = [], isPending: partnersPending } = usePartners();
  const { setPartnerCtx } = usePageContext();

  const partnerSlug = slug as PartnerSlug;
  const config = PARTNER_CONFIG[partnerSlug];

  const partner = partners.find(p => p.slug === partnerSlug);
  const partnerId = partner?.partnerId || '';
  const canEdit = canEditSection(access?.editScopes, 'all');

  useEffect(() => {
    if (!partnerId || !config) return;
    setPartnerCtx({ partnerId, partnerName: config.name, partnerSlug });
    return () => setPartnerCtx(null);
  }, [partnerId, partnerSlug]);

  const { data: products = [], isPending: listPending } = useProducts(partnerId, categoryFilter === 'all' ? undefined : categoryFilter);
  // Mientras cargan los partners la query está deshabilitada y react-query v5 da isLoading=false:
  // sin esto se veía la lista vacía hasta recargar (feedback de Partnerships 07/10/2026).
  const isLoading = partnersPending || (!!partnerId && listPending);

  const filteredProducts = useMemo(() =>
    search
      ? products.filter(p =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.tagline.toLowerCase().includes(search.toLowerCase())
        )
      : products
  , [products, search]);

  if (!config) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{t('partnerships.common.partner_not_found')}</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/partnerships')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.ui.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to={`/partnerships/${partnerSlug}`} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: config.color }} />
        <h1 className="text-2xl font-bold tracking-tight">{config.name} — {t('partnerships.products.title')}</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('partnerships.products.search_placeholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              categoryFilter === 'all'
                ? 'border-foreground bg-foreground text-background'
                : 'border-muted-foreground/30 hover:border-foreground/50'
            }`}
          >
            {t('partnerships.products.all_categories')}
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                categoryFilter === cat
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-muted-foreground/30 hover:border-foreground/50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setCreateProduct(true)}>
            <Plus className="h-3.5 w-3.5" /> {t('partnerships.products.new_button')}
          </Button>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><WinclapLoader /></div>
      ) : filteredProducts.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          {search ? t('partnerships.products.no_results') : t('partnerships.products.empty')}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map(p => (
            <ProductCard
              key={p.productId} product={p} partnerSlug={partnerSlug}
              canEdit={canEdit} onEdit={setEditProduct}
            />
          ))}
        </div>
      )}

      {partnerId && (
        <ProductFormDialog
          open={createProduct || !!editProduct}
          onOpenChange={o => { if (!o) { setCreateProduct(false); setEditProduct(undefined); } }}
          partnerId={partnerId}
          product={editProduct}
        />
      )}
    </div>
  );
}
